import React, { useCallback, useMemo } from "react";
import {
  GroundedSummaryTextFragment,
  PatientFirstSummaryFragment,
  PossibleCauses,
  SummaryTextFragment,
  WorkupActionItems,
} from "fe-common/graphql";
import { CaseStatus } from "@kahunmedical/common-wtypes";
import { useClinicState } from "ask/src/clinic/clinicStateContext";
import { ACTION, CATEGORY, evt } from "fe-common/analytics/analytics";
import { KAHUN_PLATFORM } from "user-management/src/components/loginForm";
import { PatientSummary } from "./patientSummary";
import { MedicalDataDisplayColumns } from "./MedicalDataDisplayColumns";
import { MedicalDataDisplayTabs } from "./MedicalDataDisplayTabs";
import { PatientDd } from "./patientDD";
import { PatientCardLLMText } from "./patientCardLLMText";
//import { GroundedDataParagraph } from "../grounding/grounding";
import { GroupedWorkupList } from "./groupedWorkupList";
import { useTranslateByGender } from "fe-common/locale/useTranslateByGender";
import { AlertColor } from "@mui/material/Alert";
import { AlertMessage } from "./AlertMessage";
import { ClinicConfigKeys } from "fe-common/graphql/clinic/clinicConfigKeys";
import { PatientAssessment } from "./patientAssessment";
import { useClinicMessagesContext } from "ask/src/components/clinic/clinicMessagesContext";
import { PatientCardAnswerPrompt } from "./patientCardAnswerPrompt";
import { PredefinedInsight } from "rag-chat/src/graphql/types/graphql";

//region [[ Styles ]]
//endregion [[ Styles ]]

//region [[ Props ]]

export interface MedicalDataDisplayProps {
  caseId: string;
  caseStatus: CaseStatus;
  loadingSummary: boolean;
  loadingDD: boolean;
  loadingAssessment: boolean;
  loadingWorkup: boolean;
  summary: PatientFirstSummaryFragment;
  possibleCauses: PossibleCauses;
  assessment: GroundedSummaryTextFragment;
  workupActionItems: WorkupActionItems;
  workup: SummaryTextFragment;
  error: string;
  showDD: boolean;
  onUpdate: (etag?: string) => void;
  displayMode: "columns" | "tabs";
  loading: boolean;
}

export interface MedicalDataDisplaySection {
  summarySection: React.ReactNode;
  ddSection: React.ReactNode;
  assessmentSection: React.ReactNode;
  workupSection: React.ReactNode;
  error: string;
  showDD: boolean;
  useLLM: boolean;
  useRag: boolean;
  loading: boolean;
}

//endregion [[ Props ]]

//region [[ Functions ]]
//endregion [[ Functions ]]

export const MedicalDataDisplay = ({ ...props }: MedicalDataDisplayProps) => {
  const {
    caseId,
    showDD,
    error,
    assessment,
    loadingAssessment,
    summary,
    loading,
  } = props;
  const [
    {
      clinicData: { id: clinicId, name: clinicName },
      clinicConfig,
    },
  ] = useClinicState();
  const useLLM = showDD && clinicConfig?.[ClinicConfigKeys.UsesLLM];
  const useRAG = showDD && clinicConfig?.[ClinicConfigKeys.UsesRag];
  const { setMessage } = useClinicMessagesContext();
  const { translate } = useTranslateByGender();
  const onShowMessage = useCallback(
    (text: string, severity: AlertColor) => {
      setMessage({ text, severity });
    },
    [setMessage],
  );

  const SummarySection = useMemo(
    () =>
      !loading && (
        <PatientSummary
          caseId={caseId}
          clinicId={clinicId}
          summary={summary}
          loading={props.loadingSummary}
          onUpdate={props.onUpdate}
          onNotify={(message) => {
            onShowMessage(message, null);
            evt({
              category: CATEGORY.CLINIC_MANAGEMENT,
              action: ACTION.CLINIC_COPY_PATIENT_SUMMARY,
              props: {
                clinicName,
                patientId: caseId,
              },
            });
          }}
          clinicName={clinicName}
          platform={KAHUN_PLATFORM.ClinicManage}
          hideTitle={props.displayMode === "tabs"}
        />
      ),
    [
      loading,
      caseId,
      clinicId,
      clinicName,
      props.loadingSummary,
      props.onUpdate,
      summary,
      onShowMessage,
    ],
  );

  const DDSection = useMemo(
    () =>
      !loading &&
      showDD && (
        <PatientDd
          loading={props.loadingDD}
          dd={props.possibleCauses?.possibleCauses}
          status={props.caseStatus}
          simpleView={props.displayMode === "tabs"}
          onShowMessage={onShowMessage}
          showRagDisclaimer={useRAG}
        />
      ),
    [
      loading,
      props.caseStatus,
      props.loadingDD,
      props.possibleCauses,
      showDD,
      onShowMessage,
      useRAG,
    ],
  );

  const AssessmentSection = useMemo(() => {
    if (loading) return null;
    if (error) return <AlertMessage message={error} />;
    if (useRAG) {
      return (
        <PatientCardAnswerPrompt insightType={PredefinedInsight.Assessment} />
      );
    } else {
      return (
        <PatientCardLLMText
          title={props.displayMode === "columns" && translate("ASSESSMENT")}
          loading={loadingAssessment}
          errorText={
            assessment?.error &&
            (assessment?.errorText || "Error creating assessment")
          }
          onShowMessage={onShowMessage}
        >
          <PatientAssessment assessment={assessment} />
        </PatientCardLLMText>
      );
    }
  }, [
    loading,
    assessment,
    loadingAssessment,
    error,
    translate,
    onShowMessage,
    useRAG,
  ]);

  const WorkupSection = useMemo(() => {
    if (loading) return null;
    if (error) return <AlertMessage message={error} />;
    if (useRAG) {
      return <PatientCardAnswerPrompt insightType={PredefinedInsight.Plan} />;
    } else {
      return (
        <PatientCardLLMText
          title={
            props.displayMode === "columns" && translate("SUGGESTED_WORKUP")
          }
          loading={props.loadingWorkup}
          errorText={
            props.workup?.error &&
            (props.workup?.errorText || "Error creating workup")
          }
          onShowMessage={onShowMessage}
        >
          {props.workupActionItems?.items?.length ? (
            <GroupedWorkupList workupList={props.workupActionItems?.items} />
          ) : (
            !props.loadingWorkup && props.workup?.text
          ) //show workup text if structured list was not found
          }
        </PatientCardLLMText>
      );
    }
  }, [
    props.workup,
    props.workupActionItems,
    props.loadingWorkup,
    error,
    translate,
    onShowMessage,
    useRAG,
    loading,
  ]);

  return props.displayMode === "columns" ? (
    <MedicalDataDisplayColumns
      summarySection={SummarySection}
      ddSection={DDSection}
      assessmentSection={AssessmentSection}
      workupSection={WorkupSection}
      error={error}
      showDD={showDD}
      useLLM={useLLM}
      useRag={useRAG}
      loading={loading}
    />
  ) : (
    <MedicalDataDisplayTabs
      summarySection={SummarySection}
      ddSection={DDSection}
      assessmentSection={AssessmentSection}
      workupSection={WorkupSection}
      error={error}
      showDD={showDD}
      useLLM={useLLM}
      useRag={useRAG}
      loading={loading}
    />
  );
};
